import {ActionList, Box, Button} from '@primer/react'
import {forwardRef, type FunctionComponent, type PropsWithChildren, type ReactNode} from 'react'
import type {IconProps} from '@primer/octicons-react'

export type SharedPickerProps = {
  //Props used in the case when there is no selection yet
  leadingIcon?: FunctionComponent<PropsWithChildren<IconProps>>
  hotKey?: string
  //Props used in the case when there is selection
  leadingIconElement?: ReactNode
  sharedPickerMainValue?: ReactNode
  size?: 'small' | 'large'
  // Props used in both cases
  anchorText: string
  anchorProps?: React.HTMLAttributes<HTMLElement>
  ariaLabelledBy?: string
  ariaLabel?: string
  readonly?: boolean
  /**
   * Whether to render the picker as a nested menu item (true) versus a standalone button (false; default).
   */
  nested?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export const SharedPicker = forwardRef<HTMLButtonElement, SharedPickerProps>(
  (
    {
      leadingIconElement,
      anchorText,
      anchorProps,
      sharedPickerMainValue,
      ariaLabel,
      readonly,
      size,
      nested = false,
      leadingIcon: LeadingIcon,
      hotKey,
      ariaLabelledBy,
    },
    ref,
  ) => {
    const sharedProps = {
      'aria-labelledby': ariaLabelledBy,
      'aria-label': ariaLabel,
      'aria-disabled': readonly,
      disabled: readonly,
    }

    if (nested) {
      const leadingIcon = sharedPickerMainValue ? leadingIconElement : LeadingIcon && <LeadingIcon />

      return (
        <ActionList.Item {...anchorProps} {...sharedProps} role="menuitem">
          {leadingIcon && <ActionList.LeadingVisual>{leadingIcon}</ActionList.LeadingVisual>}
          {anchorText}
        </ActionList.Item>
      )
    }

    if (sharedPickerMainValue) {
      return (
        <Button size={size ? size : 'small'} {...anchorProps} {...sharedProps} ref={ref}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Box sx={{color: 'fg.muted', fontWeight: 'normal'}}>{anchorText}</Box>
            <Box
              sx={{
                display: leadingIconElement ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                '> *:not(:last-child)': {
                  mr: '-3px',
                },
              }}
            >
              {leadingIconElement}
            </Box>
            <Box sx={{fontWeight: 'bold'}}>{sharedPickerMainValue}</Box>
          </Box>
        </Button>
      )
      //  This used to be SharedEmptyPicker
    } else {
      return (
        <Button
          ref={ref}
          sx={{borderStyle: 'dashed', fontWeight: 'normal', color: 'fg.muted'}}
          size={'small'}
          leadingVisual={LeadingIcon}
          trailingVisual={
            hotKey
              ? () => (
                  <Box
                    sx={{
                      color: 'fg.muted',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: 'border.default',
                      p: '0px 4px',
                      borderRadius: '6px',
                    }}
                  >
                    {hotKey}
                  </Box>
                )
              : undefined
          }
          {...sharedProps}
          {...anchorProps}
        >
          {anchorText}
        </Button>
      )
    }
  },
)

SharedPicker.displayName = 'SharedPicker'
